import React, { useEffect, useState, useRef } from 'react';
import Pusher from 'pusher-js';
import { useParams } from 'react-router-dom';
import {
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/20/solid';
import NoOrder from '../../../assets/no-orders.svg';
import {
  CheckIcon,
  ReceiptRefundIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import orderApi from '../../../services/orderApi';
import moment from 'moment';
import NotificationSound from '../../../assets/notification-sound.mp3';
import { OrderStatus, PaymentStatus } from '../../../data/enums';
import {
  formatOptionName,
  getExtendedOptionPrice,
} from '../../../utils/optionDisplay';
import { inMenuOrder, optionsInMenuOrder } from '../../../utils/menu-order';
import {
  taxAndFeesTotal,
  extraFeeRows,
  lineAmount,
} from '../../../utils/orderTotals';
import { SkeletonBlock } from '../../../components/skeleton/skeleton';
import {
  IconBadge,
  TWButton,
  TWModal,
  TWPagination,
} from '../../../components/ui';

const cn = (...c) => c.filter(Boolean).join(' ');

const usd = (val) =>
  (val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/* ----------------------------- Glyphs ----------------------------- */

const DineInGlyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 3v6" />
    <path d="M10 3v6a3 3 0 0 1-3 3v9" />
    <path d="M4 3v6a3 3 0 0 0 3 3" />
    <path d="M18 3c-1.5 0-3 2-3 5s1.5 4 3 4" />
    <path d="M18 12v9" />
  </svg>
);

const ToGoGlyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8h12l-1 12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 8z" />
    <path d="M9 8V5a3 3 0 0 1 6 0v3" />
    <path d="M9.5 13h5" />
  </svg>
);

const OrderTypeGlyph = ({ type, className }) =>
  type === 10 ? (
    <DineInGlyph className={className} />
  ) : (
    <ToGoGlyph className={className} />
  );

const orderTypeGradient = (type) =>
  type === 10
    ? 'bg-gradient-to-br from-fuchsia-500 via-purple-600 to-indigo-700'
    : 'bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700';

const orderTypeLabel = (type) => (type === 10 ? 'DINE IN' : 'TO GO');

const orderStatusBadge = (status) => {
  switch (status) {
    case 0:
      return 'bg-amber-500 text-white shadow-amber-500/30';
    case 10:
      return 'bg-blue-600 text-white shadow-blue-500/30';
    case 20:
      return 'bg-purple-600 text-white shadow-purple-500/30';
    case 30:
      return 'bg-slate-600 text-white shadow-slate-500/30';
    case 40:
      return 'bg-rose-600 text-white shadow-rose-500/30';
    case 100:
      return 'bg-emerald-600 text-white shadow-emerald-500/30';
    default:
      return 'bg-slate-600 text-white shadow-slate-500/30';
  }
};

const inputClass =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/30';

const eyebrow =
  'text-[0.6rem] font-black uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400';

function LiveOrders() {
  const { storeid } = useParams();
  const [selectedExtraTimes, setSelectedExtraTimes] = useState(null);
  const [dialogs, setDialogs] = useState({
    refund: false,
    taxandFees: false,
  });
  const [form, setForm] = useState({
    refund: 0,
  });
  const [errors, setErrors] = useState({
    refund: false,
  });
  const [loading, setLoading] = useState({
    orderList: true,
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
  });
  const [orderList, setOrderList] = useState([]);
  const [tabs, setTabs] = useState([
    { name: 'Active', current: true },
    { name: 'Completed', current: false },
  ]);

  const extraTimes = [
    { name: '10', value: 10 },
    { name: '15', value: 15 },
    { name: '20', value: 20 },
    { name: '25', value: 25 },
    { name: '30+', value: 30 },
  ];

  const audioPlayer = useRef(null);

  const onClickSelectOrder = (order) => {
    setSelectedOrder(order);
    setSelectedExtraTimes(null);
  };

  let orderstatuses;
  const currentTab = tabs.find((tab) => tab.current).name;
  if (currentTab === 'Active') {
    orderstatuses = [0, 10, 20];
  } else {
    orderstatuses = [30, 40, 100];
  }

  const getOrderList = async () => {
    setLoading((prev) => ({ ...prev, orderList: true }));

    const query = {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      deleted: false,
      storeid: storeid,
      orderstatuses,
      paymentstatus: 20,
    };

    try {
      const list = await orderApi.list(query);
      if (list.data.data.length > 0) {
        setOrderList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      } else {
        setOrderList([]);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, orderList: false }));
    }
  };

  const onChangeOrderStatus = async (orderstatus, acceptplus) => {
    try {
      const body = {
        orderid: selectedOrder.id,
        orderstatus,
        acceptplus: acceptplus || 0,
      };
      const changedStatusOrder = await orderApi.changeOrderStatus(body);
      // ChangeStatus returns the order without its extrafees rows; keep the
      // ones the list already loaded so the per-fee rows under Tax & Fees
      // don't collapse to the single "Fees" fallback.
      setSelectedOrder((prev) => ({
        ...changedStatusOrder.data.data,
        extrafees: changedStatusOrder.data.data?.extrafees ?? prev?.extrafees,
      }));
    } catch (error) {
    } finally {
      setSelectedExtraTimes(null);
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickRefund = () => {
    setForm({
      refund: (selectedOrder.total - selectedOrder.refund).toFixed(2),
      orderstatus: 40,
      orderid: selectedOrder.id,
    });
    setDialogs((prev) => ({ ...prev, refund: true }));
  };

  const onFormRefundSubmit = async () => {
    if (!form.refund || form.refund > selectedOrder.total || form.refund < 0) {
      setErrors((prev) => ({
        ...prev,
        refund:
          !form.refund || form.refund > selectedOrder.total || form.refund < 0
            ? true
            : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, refund: false }));
    }

    try {
      setLoading((prev) => ({ ...prev, orderList: true }));
      const body = { ...form };
      await orderApi.changeOrderStatus(body);
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, orderList: false }));
      setDialogs((prev) => ({ ...prev, refund: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, refund: false }));
    setDialogs((prev) => ({ ...prev, refund: false }));
  };

  const calculateItemsCount = (items) => {
    const itemsCount = items.reduce((total, item) => total + item.quantity, 0);
    return itemsCount > 1 ? `${itemsCount} items` : `${itemsCount} item`;
  };

  useEffect(() => {
    getOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, pagination.pageNumber]);

  useEffect(() => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_TOKEN, {
      cluster: 'us3',
    });
    const channel = pusher.subscribe(storeid);

    const callback = (eventName) => {
      if (eventName === 'new_order') {
        audioPlayer.current.play();
        getOrderList();
      } else if (eventName === 'update_order') {
        getOrderList();
      }
    };
    channel.bind_global(callback);

    return () => {
      pusher.unsubscribe(storeid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatOrderDate = (order) => {
    if (!order) return '';
    if (order.store?.timezoneid) {
      return moment(order.createdate)
        .tz(order.store.timezoneid)
        .format('MM/DD/YYYY hh:mm A');
    }
    return (
      moment(order.createdate)
        .utcOffset(order?.timezone * 60)
        .format('MM/DD/YYYY hh:mm A') || ''
    );
  };

  const renderOrderListSkeleton = () => (
    <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
      {[...Array(5)].map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-4 py-4">
          <SkeletonBlock className="h-12 w-12 !rounded-md" />
          <div className="flex-1 space-y-2">
            <SkeletonBlock className="h-5 w-24" />
            <SkeletonBlock className="h-3 w-32" />
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* ============ SIDEBAR ============ */}
          <aside
            className={cn(
              selectedOrder ? 'hidden lg:block' : 'block',
              'lg:col-span-1',
            )}
          >
            <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
              {/* Header */}
              <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className={eyebrow}>Kitchen</p>
                    <h1 className="mt-0.5 text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
                      Live Orders
                    </h1>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-[0.6rem] font-black uppercase tracking-[0.22em] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    Live
                  </span>
                </div>

                {/* Tabs */}
                <div className="mt-4 inline-flex rounded-md bg-slate-100 p-1 dark:bg-slate-900/60">
                  {tabs.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() =>
                        setTabs(
                          tabs.map((tab) =>
                            tab.name === item.name
                              ? { ...tab, current: true }
                              : { ...tab, current: false },
                          ),
                        )
                      }
                      className={cn(
                        'rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition',
                        item.current
                          ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      )}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Order list */}
              <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
                {loading.orderList ? (
                  renderOrderListSkeleton()
                ) : orderList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                    <img src={NoOrder} className="h-20 opacity-80" alt="" />
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      No {currentTab.toLowerCase()} orders
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {orderList.map((order) => {
                      const isSelected = order.id === selectedOrder?.id;
                      return (
                        <li
                          key={order.id}
                          onClick={() => onClickSelectOrder(order)}
                          className={cn(
                            'relative cursor-pointer transition',
                            isSelected
                              ? 'bg-blue-50/70 dark:bg-sky-500/10'
                              : 'hover:bg-slate-50/70 dark:hover:bg-slate-700/30',
                          )}
                        >
                          {isSelected && (
                            <span className="absolute inset-y-0 left-0 w-1 bg-blue-600 dark:bg-sky-400" />
                          )}
                          <div className="grid grid-cols-[auto_1fr] items-center gap-3 px-4 py-3.5">
                            <div
                              className={cn(
                                orderTypeGradient(order.ordertype),
                                'flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-white shadow-md shadow-blue-500/20',
                              )}
                            >
                              <OrderTypeGlyph
                                type={order.ordertype}
                                className="h-6 w-6"
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-2 min-w-0">
                                  <span className="truncate font-mono text-lg font-black tracking-tight text-slate-900 dark:text-slate-100">
                                    {order.ordernumberstr}
                                  </span>
                                  <span className={cn(eyebrow, 'shrink-0')}>
                                    {orderTypeLabel(order.ordertype)}
                                  </span>
                                </div>
                                <span
                                  className={cn(
                                    orderStatusBadge(order.orderstatus),
                                    'inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-[0.12em] shadow-sm',
                                  )}
                                >
                                  {order.orderstatus === 40 &&
                                  order.refund !== order.total
                                    ? 'Partial'
                                    : OrderStatus[order.orderstatus]}
                                </span>
                              </div>
                              <div className="mt-1 flex items-center justify-between gap-2 text-xs">
                                <p className="flex min-w-0 items-center gap-1 text-slate-500 dark:text-slate-400">
                                  <UserIcon className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">
                                    {order.name ||
                                      order.customer?.fullname ||
                                      '—'}
                                  </span>
                                </p>
                                <p className="whitespace-nowrap font-mono font-bold tabular-nums text-slate-900 dark:text-slate-100">
                                  {usd(order.total)}
                                  <span className="ml-1 font-sans font-normal text-slate-400 dark:text-slate-500">
                                    · {calculateItemsCount(order.orderitems)}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Pagination */}
              {!loading.orderList && orderList.length > 0 && (
                <div className="flex items-center justify-center border-t border-slate-100 px-4 py-3 dark:border-slate-700">
                  <TWPagination
                    onChange={(page) =>
                      setPagination((prev) => ({ ...prev, pageNumber: page }))
                    }
                    simple
                    current={pagination.pageNumber}
                    pageSize={pagination.pageSize}
                    total={pagination.totalRecords}
                  />
                </div>
              )}
            </div>
          </aside>

          {/* ============ DETAIL ============ */}
          {selectedOrder ? (
            <section className="lg:col-span-2">
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                {/* Mobile back bar */}
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700 lg:hidden">
                  <button
                    type="button"
                    onClick={() => setSelectedOrder(null)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                    aria-label="Back to orders"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="font-mono text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">
                    {selectedOrder.ordernumberstr}
                  </span>
                  <span className="w-9" aria-hidden />
                </div>

                {/* Hero header */}
                <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white px-5 py-6 dark:border-slate-700 dark:from-slate-900/60 dark:via-slate-800 dark:to-slate-800 sm:px-7">
                  {/* decorative grid */}
                  <div
                    className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
                    style={{
                      backgroundImage:
                        'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
                      backgroundSize: '24px 24px',
                    }}
                  />

                  <div className="relative flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          orderTypeGradient(selectedOrder.ordertype),
                          'flex h-16 w-16 shrink-0 items-center justify-center rounded-md text-white shadow-lg shadow-blue-500/30',
                        )}
                      >
                        <OrderTypeGlyph
                          type={selectedOrder.ordertype}
                          className="h-9 w-9"
                        />
                      </div>
                      <div>
                        <p className={eyebrow}>Order</p>
                        <h2 className="font-mono text-4xl font-black tracking-tighter text-slate-900 dark:text-slate-100 sm:text-5xl">
                          {selectedOrder.ordernumberstr}
                        </h2>
                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                          {orderTypeLabel(selectedOrder.ordertype)}
                          {selectedOrder.table && (
                            <span className="ml-1 text-slate-400 dark:text-slate-500">
                              · Table {selectedOrder.table}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={cn(
                          orderStatusBadge(selectedOrder.orderstatus),
                          'inline-flex items-center rounded-md px-3 py-1 text-xs font-black uppercase tracking-[0.15em] shadow-sm',
                        )}
                      >
                        {selectedOrder.orderstatus === 40 &&
                        selectedOrder.refund !== selectedOrder.total
                          ? 'Partial Refund'
                          : OrderStatus[selectedOrder.orderstatus]}
                      </span>
                      <span className="inline-flex items-center rounded-md border-2 border-dashed border-slate-300 px-2 py-0.5 font-mono text-[0.65rem] font-black uppercase tracking-[0.15em] text-slate-500 dark:border-slate-600 dark:text-slate-400">
                        {PaymentStatus[selectedOrder.paymentstatus]}
                      </span>
                    </div>
                  </div>

                  {/* Meta row */}
                  <div className="relative mt-5 grid grid-cols-1 gap-2 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                    {selectedOrder.name && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="truncate font-medium">
                          {selectedOrder.name}
                        </span>
                      </div>
                    )}
                    {selectedOrder.phone && (
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="truncate font-mono tabular-nums">
                          {selectedOrder.phone}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <BuildingStorefrontIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="truncate font-medium">
                        {selectedOrder.store.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="truncate font-mono tabular-nums">
                        {formatOrderDate(selectedOrder)}
                      </span>
                    </div>
                    {selectedOrder.terminal?.name && (
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="truncate font-medium">
                          {selectedOrder.terminal.name}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 sm:justify-end">
                      <span className={eyebrow}>Order #</span>
                      <span className="font-mono font-bold tabular-nums text-slate-700 dark:text-slate-200">
                        {selectedOrder.orderid}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action bar — Pending (status 0) */}
                {selectedOrder.orderstatus === 0 && (
                  <div className="border-b border-slate-200 bg-amber-50/50 px-5 py-4 dark:border-slate-700 dark:bg-amber-500/5">
                    <p className="mb-3 text-[0.6rem] font-black uppercase tracking-[0.22em] text-amber-700 dark:text-amber-300">
                      Confirm · Select prep time (min)
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {extraTimes.map((time) => (
                        <button
                          key={time.value}
                          type="button"
                          onClick={() => onChangeOrderStatus(10, time.value)}
                          className={cn(
                            'inline-flex min-w-[3.5rem] items-center justify-center gap-1 rounded-md border px-3 py-2 font-mono text-sm font-black transition',
                            time.value === selectedExtraTimes
                              ? 'border-emerald-500 bg-emerald-500 text-white shadow-md shadow-emerald-500/30'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300',
                          )}
                        >
                          {time.name}
                        </button>
                      ))}
                      <div className="ml-auto">
                        <button
                          type="button"
                          onClick={() => onChangeOrderStatus(30)}
                          className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-4 py-2 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-rose-500/30 transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action bar — other statuses */}
                {selectedOrder.orderstatus !== 0 && (
                  <div className="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                    {selectedOrder.orderstatus === 10 && (
                      <button
                        type="button"
                        onClick={() => onChangeOrderStatus(20)}
                        className="inline-flex items-center gap-1.5 rounded-md border-2 border-purple-500 bg-white px-4 py-1.5 text-xs font-black uppercase tracking-wider text-purple-700 shadow-sm transition hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500/30 dark:bg-slate-800 dark:text-purple-300 dark:hover:bg-purple-500/10"
                      >
                        <CheckIcon className="h-4 w-4" />
                        Mark Ready
                      </button>
                    )}
                    {selectedOrder.orderstatus === 20 && (
                      <button
                        type="button"
                        onClick={() => onChangeOrderStatus(100)}
                        className="inline-flex items-center gap-1.5 rounded-md border-2 border-emerald-500 bg-white px-4 py-1.5 text-xs font-black uppercase tracking-wider text-emerald-700 shadow-sm transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:bg-slate-800 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                      >
                        <CheckIcon className="h-4 w-4" />
                        Complete
                      </button>
                    )}
                    {![30, 40].includes(selectedOrder.orderstatus) && (
                      <button
                        type="button"
                        onClick={onClickRefund}
                        className="inline-flex items-center gap-1.5 rounded-md border-2 border-rose-500 bg-white px-4 py-1.5 text-xs font-black uppercase tracking-wider text-rose-700 shadow-sm transition hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-500/30 dark:bg-slate-800 dark:text-rose-300 dark:hover:bg-rose-500/10"
                      >
                        <ReceiptRefundIcon className="h-4 w-4" />
                        Refund
                      </button>
                    )}
                  </div>
                )}

                {/* Perforation divider */}
                <div className="relative h-4 bg-white dark:bg-slate-800">
                  <div
                    className="absolute inset-x-5 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-slate-200 dark:border-slate-700"
                    aria-hidden
                  />
                  <div className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white ring-1 ring-slate-200 dark:bg-slate-900/40 dark:ring-slate-700" />
                  <div className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white ring-1 ring-slate-200 dark:bg-slate-900/40 dark:ring-slate-700" />
                </div>

                {/* Items */}
                <div className="px-5 py-5 sm:px-7">
                  <div className="mb-3 grid grid-cols-12 items-center gap-2 text-[0.6rem] font-black uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                    <div className="col-span-2 sm:col-span-1">Qty</div>
                    <div className="col-span-7 sm:col-span-9">Item</div>
                    <div className="col-span-3 sm:col-span-2 text-right">
                      Price
                    </div>
                  </div>
                  <div className="border-t-2 border-dashed border-slate-300 dark:border-slate-600">
                    {inMenuOrder(selectedOrder.orderitems).map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 items-start gap-2 border-b border-dashed border-slate-200 py-3 dark:border-slate-700"
                      >
                        <div className="col-span-2 sm:col-span-1">
                          <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md bg-slate-900 px-2 font-mono text-xs font-black tabular-nums text-white dark:bg-slate-200 dark:text-slate-900">
                            {item.quantity}×
                          </span>
                        </div>
                        <div className="col-span-7 sm:col-span-9">
                          <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                            {item.item.product.name}
                          </p>
                          {item.item.product.name !== item.item.name && (
                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              {item.item.name}{' '}
                              <span className="font-mono italic">
                                {usd(item.item.price + selectedOrder?.itemfee)}
                              </span>
                            </p>
                          )}
                          {item.orderitemoptions.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {optionsInMenuOrder(item.orderitemoptions).map((option, i) => (
                                <li
                                  key={i}
                                  className="text-xs text-slate-500 dark:text-slate-400"
                                >
                                  <span className="font-medium">
                                    + {formatOptionName(option, item.quantity)}
                                  </span>{' '}
                                  <span className="font-mono italic">
                                    {usd(
                                      getExtendedOptionPrice(
                                        option,
                                        item.quantity,
                                      ),
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.note && (
                            <p className="mt-1 inline-block rounded-md border-2 border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-xs italic text-amber-800 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-200">
                              "{item.note}"
                            </p>
                          )}
                        </div>
                        <div className="col-span-3 sm:col-span-2 text-right font-mono text-sm font-black tabular-nums text-slate-900 dark:text-slate-100">
                          {usd(lineAmount(item, selectedOrder?.itemfee))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Note + totals */}
                  <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
                    <div className="lg:col-span-3">
                      {selectedOrder.note ? (
                        <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50/60 px-3 py-2.5 dark:border-slate-600 dark:bg-slate-900/40">
                          <p className={eyebrow}>Order note</p>
                          <p className="mt-1 text-sm italic text-slate-700 dark:text-slate-200">
                            "{selectedOrder.note}"
                          </p>
                        </div>
                      ) : (
                        <div />
                      )}
                    </div>
                    <div className="lg:col-span-2">
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between text-slate-600 dark:text-slate-300">
                          <dt>Subtotal</dt>
                          <dd className="font-mono tabular-nums">
                            {usd(selectedOrder.subtotal)}
                          </dd>
                        </div>
                        <div className="flex justify-between text-slate-600 dark:text-slate-300">
                          <dt className="flex items-center gap-1">
                            Tax &amp; Fees
                            <button
                              type="button"
                              onClick={() =>
                                setDialogs((prev) => ({
                                  ...prev,
                                  taxandFees: !prev.taxandFees,
                                }))
                              }
                              className="text-slate-400 transition hover:text-blue-600 dark:hover:text-sky-400"
                              aria-label="Tax and fees info"
                            >
                              <InformationCircleIcon className="h-4 w-4" />
                            </button>
                          </dt>
                          <dd className="font-mono tabular-nums">
                            {usd(taxAndFeesTotal(selectedOrder))}
                          </dd>
                        </div>
                        {extraFeeRows(selectedOrder).map((fee, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-slate-600 dark:text-slate-300"
                          >
                            <dt>{fee.name}</dt>
                            <dd className="font-mono tabular-nums">
                              {usd(fee.price)}
                            </dd>
                          </div>
                        ))}
                        {selectedOrder.tip !== 0 && (
                          <div className="flex justify-between text-slate-600 dark:text-slate-300">
                            <dt>Tip</dt>
                            <dd className="font-mono tabular-nums">
                              {usd(selectedOrder.tip)}
                            </dd>
                          </div>
                        )}
                        {selectedOrder.discount !== 0 && (
                          <div className="flex justify-between text-rose-600 dark:text-rose-400">
                            <dt>Discount</dt>
                            <dd className="font-mono tabular-nums">
                              -{usd(selectedOrder.discount)}
                            </dd>
                          </div>
                        )}
                        {selectedOrder.refund !== 0 && (
                          <div className="flex justify-between text-rose-600 dark:text-rose-400">
                            <dt>Refund</dt>
                            <dd className="font-mono tabular-nums">
                              -{usd(selectedOrder.refund)}
                            </dd>
                          </div>
                        )}
                        <div className="!mt-3 flex items-baseline justify-between border-t-2 border-double border-slate-900 pt-3 dark:border-slate-100">
                          <dt className={eyebrow}>Total</dt>
                          <dd className="font-mono text-2xl font-black tabular-nums text-slate-900 dark:text-slate-100">
                            {usd(
                              selectedOrder.refund
                                ? selectedOrder.total - selectedOrder.refund
                                : selectedOrder.total,
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section className="hidden lg:col-span-2 lg:block">
              <div className="flex h-full min-h-[24rem] flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-600 dark:bg-slate-800">
                <img src={NoOrder} className="h-24 opacity-80" alt="" />
                <p className={eyebrow}>Nothing selected</p>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Pick an order from the queue to view its ticket
                </p>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* ============ REFUND MODAL ============ */}
      <TWModal
        open={dialogs.refund}
        onClose={onCloseUpsert}
        size="md"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.orderList}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="danger"
              label="Refund"
              disabled={loading.orderList}
              loading={loading.orderList}
              onClick={onFormRefundSubmit}
            />
          </div>
        }
      >
        <div className="flex items-start gap-3 border-b border-slate-200 bg-gradient-to-br from-rose-50 to-white px-5 py-4 dark:border-slate-700 dark:from-rose-500/10 dark:to-slate-800">
          <IconBadge icon={ReceiptRefundIcon} tone="rose" size="md" />
          <div>
            <h3 className="text-base font-black tracking-tight text-slate-900 dark:text-slate-100">
              Issue Refund
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Refund will be processed immediately
            </p>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label htmlFor="refund" className={cn(eyebrow, 'mb-1.5 block')}>
              Refund Amount
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                $
              </span>
              <input
                value={form.refund}
                onChange={(e) => onFormChange(e.target.value, 'refund')}
                type="number"
                name="refund"
                id="refund"
                className={cn(inputClass, 'pl-7 font-mono')}
                placeholder="0.00"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Available balance:{' '}
              <span className="font-mono font-bold text-slate-700 dark:text-slate-200">
                {usd(selectedOrder?.total - selectedOrder?.refund)}
              </span>
            </p>
            {errors.refund && (
              <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
                Please enter a valid refund amount
              </p>
            )}
          </div>
        </div>
      </TWModal>

      {/* ============ TAX & FEES MODAL ============ */}
      <TWModal
        open={dialogs.taxandFees}
        onClose={() =>
          setDialogs((prev) => ({ ...prev, taxandFees: !prev.taxandFees }))
        }
        size="lg"
        showCloseButton
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            <TWButton
              variant="primary"
              label="Got it"
              onClick={() =>
                setDialogs((prev) => ({
                  ...prev,
                  taxandFees: !prev.taxandFees,
                }))
              }
            />
          </div>
        }
      >
        <div className="flex items-start gap-3 border-b border-slate-200 bg-gradient-to-br from-blue-50 to-white px-5 py-4 dark:border-slate-700 dark:from-sky-500/10 dark:to-slate-800">
          <IconBadge icon={InformationCircleIcon} tone="blue" size="md" />
          <div>
            <h3 className="text-base font-black tracking-tight text-slate-900 dark:text-slate-100">
              Sales Tax &amp; Fees
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Breakdown of charges on this order
            </p>
          </div>
        </div>

        <div className="space-y-3 px-5 py-5">
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Sales Tax{' '}
                {selectedOrder?.store?.tax
                  ? `(${selectedOrder.store.tax}%)`
                  : ''}
              </span>
              <span className="font-mono text-sm font-black tabular-nums text-slate-900 dark:text-slate-100">
                {usd(selectedOrder?.tax)}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Sales tax is added.
            </p>
          </div>

          {selectedOrder?.crvtax !== 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                  CRV Tax
                </span>
                <span className="font-mono text-sm font-black tabular-nums text-slate-900 dark:text-slate-100">
                  {usd(selectedOrder?.crvtax)}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                The CRV fee, included in the price of beverages in California.
              </p>
            </div>
          )}

          {selectedOrder?.fee !== 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                  Platform Fee
                </span>
                <span className="font-mono text-sm font-black tabular-nums text-slate-900 dark:text-slate-100">
                  {usd(selectedOrder?.fee)}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Retained by Thisway to cover the costs of developing,
                maintaining, and operating the kiosk ordering platform. This fee
                is not a tip or service charge and collected by Thisway, not
                restaurant.
              </p>
            </div>
          )}
        </div>
      </TWModal>

      <audio ref={audioPlayer} src={NotificationSound} />
    </div>
  );
}

export default LiveOrders;
